import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBell, faShieldHalved, faBoxOpen, faCode, faCheckCircle, faTimesCircle, faCopy, faTerminal } from '@fortawesome/free-solid-svg-icons';
import { SectionTitle, CodeBlock, InfoCard, useApiDocs } from '../../../components/apidocs';
import {
  WEBHOOK_BUILDER_EVENTS, buildBuilderPayloads,
  WEBHOOK_CODEGEN_LANGUAGES, WEBHOOK_HANDLER_SAMPLES,
} from '../data/webhooks';
import type { WebhookToolboxController, WebhookToolTab } from '../hooks/useWebhookToolbox';

const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 text-sm font-mono focus:border-fuchsia-300 focus:ring-2 focus:ring-fuchsia-100 dark:focus:ring-slate-600 outline-none';
const LABEL_CLASS = 'block text-xs font-bold text-gray-700 dark:text-slate-200 mb-1.5';

const TOOL_TABS: Array<{ id: WebhookToolTab; icon: IconDefinition; label: string }> = [
  { id: 'verifier', icon: faShieldHalved, label: 'Signature Verifier' },
  { id: 'builder', icon: faBoxOpen, label: 'Payload Builder' },
  { id: 'codegen', icon: faCode, label: 'Code Generator' },
];

/* ── แท็บย่อย: ตรวจสอบ signature ─────────────────────────── */
const SignatureVerifier: React.FC<{ tools: WebhookToolboxController }> = ({ tools }) => {
  const { verifyPayload, setVerifyPayload, verifySignature, setVerifySignature, verifySecret, setVerifySecret, verifyResult, verify } = tools;

  return (
    <div>
      <InfoCard type="info" title="วิธีใช้">
        วาง Webhook Payload, Signature (จาก header <code className="bg-sky-100 dark:bg-sky-900/40 px-1 rounded text-xs font-mono">X-Webhook-Signature</code>) และ Secret → กด Verify เพื่อตรวจสอบ
      </InfoCard>

      <div className="space-y-4 mb-6">
        <div>
          <label className={LABEL_CLASS}>Webhook Secret</label>
          <input
            type="text"
            value={verifySecret}
            onChange={e => setVerifySecret(e.target.value)}
            placeholder="your_webhook_secret"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Payload (JSON body ที่ได้รับ)</label>
          <textarea
            value={verifyPayload}
            onChange={e => setVerifyPayload(e.target.value)}
            rows={6}
            placeholder='{"event":"order.paid","order_number":"ORD_abc123",...}'
            className={`${INPUT_CLASS} resize-y`}
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Signature (จาก X-Webhook-Signature header)</label>
          <input
            type="text"
            value={verifySignature}
            onChange={e => setVerifySignature(e.target.value)}
            placeholder="abc123def456..."
            className={INPUT_CLASS}
          />
        </div>
      </div>

      <button
        onClick={verify}
        className="w-full py-3 rounded-xl font-bold text-sm text-white shadow-lg transition-all flex items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 hover:shadow-xl hover:-translate-y-0.5"
      >
        <FontAwesomeIcon icon={faShieldHalved} /> Verify Signature
      </button>

      {verifyResult && (
        <div className={`mt-5 rounded-xl border p-5 ${verifyResult.valid ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800'}`}>
          <div className={`flex items-center gap-2 font-bold text-sm mb-3 ${verifyResult.valid ? 'text-emerald-700' : 'text-rose-700'}`}>
            <FontAwesomeIcon icon={verifyResult.valid ? faCheckCircle : faTimesCircle} className="text-lg" />
            {verifyResult.valid ? 'Signature ถูกต้อง! ✅' : 'Signature ไม่ตรง! ❌'}
          </div>
          {!verifyResult.valid && (
            <div className="space-y-2 text-xs">
              <div>
                <span className="font-bold text-gray-600 dark:text-slate-300">ที่ได้รับ: </span>
                <code className="bg-rose-100 px-2 py-0.5 rounded font-mono text-rose-700 break-all">{verifySignature || '(ว่าง)'}</code>
              </div>
              <div>
                <span className="font-bold text-gray-600 dark:text-slate-300">ที่ถูกต้อง: </span>
                <code className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-emerald-700 break-all">{verifyResult.expected}</code>
              </div>
              <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-rose-100 dark:border-rose-900/40">
                <p className="font-bold text-gray-700 dark:text-slate-200 mb-1">สาเหตุที่พบบ่อย:</p>
                <ul className="list-disc list-inside text-gray-500 dark:text-slate-400 space-y-0.5">
                  <li>Webhook Secret ไม่ตรงกับที่ตั้งไว้ในระบบ</li>
                  <li>Payload ถูกแก้ไขก่อน verify (เช่น parse แล้ว stringify ใหม่ ทำให้ key order เปลี่ยน)</li>
                  <li>ใช้ raw body ตรงๆ ที่ได้รับ ห้ามแปลงก่อน verify</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── แท็บย่อย: สร้าง payload ตัวอย่าง ─────────────────────── */
const PayloadBuilder: React.FC<{ tools: WebhookToolboxController }> = ({ tools }) => {
  const { copyCode } = useApiDocs();
  const { builderEvent, setBuilderEvent, copyCurlWithSignature } = tools;

  const payloads = buildBuilderPayloads();
  const payload = payloads[builderEvent] || payloads['order.paid'];
  const payloadStr = JSON.stringify(payload, null, 2);

  return (
    <div>
      <InfoCard type="tip" title="Payload Builder">
        เลือก Event Type แล้ว copy payload ตัวอย่างไปทดสอบกับ webhook handler ของคุณ
      </InfoCard>

      <div className="flex flex-wrap gap-2 mb-6">
        {WEBHOOK_BUILDER_EVENTS.map(ev => (
          <button
            key={ev.id}
            onClick={() => setBuilderEvent(ev.id)}
            className={`px-4 py-2 rounded-lg text-xs font-bold font-mono border transition-all ${
              builderEvent === ev.id
                ? `bg-${ev.color}-50 text-${ev.color}-700 border-${ev.color}-200 shadow-sm`
                : 'bg-white dark:bg-slate-700 text-gray-500 dark:text-slate-300 border-gray-200 dark:border-slate-600 hover:border-gray-300'
            }`}
          >
            {ev.label}
          </button>
        ))}
      </div>

      <CodeBlock code={payloadStr} label={`${builderEvent}.json`} id={`wh-build-${builderEvent}`} />

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => copyCode(payloadStr, `wh-payload-${builderEvent}`)}
          className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
        >
          <FontAwesomeIcon icon={faCopy} /> Copy Payload
        </button>
        <button
          onClick={() => copyCurlWithSignature(payload)}
          className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-fuchsia-200 text-fuchsia-700 bg-fuchsia-50 hover:bg-fuchsia-100 transition-all"
        >
          <FontAwesomeIcon icon={faTerminal} /> Copy as cURL + Signature
        </button>
      </div>

      <div className="mt-4">
        <InfoCard type="tip" title="ทดสอบ Local">
          <span>copy cURL ด้านบนไปรันใน terminal เพื่อยิง webhook เข้า server ของคุณ เช่น </span>
          <code className="bg-violet-100 dark:bg-violet-900/40 px-1 rounded text-xs font-mono">http://localhost:3000/webhook</code>
        </InfoCard>
      </div>
    </div>
  );
};

/* ── แท็บย่อย: สร้างโค้ด handler ──────────────────────────── */
const CodeGenerator: React.FC<{ tools: WebhookToolboxController }> = ({ tools }) => {
  const { codeLang, setCodeLang } = tools;
  const lang = WEBHOOK_CODEGEN_LANGUAGES.find(l => l.id === codeLang);

  return (
    <div>
      <InfoCard type="info" title="Webhook Handler Generator">
        เลือกภาษาแล้ว copy code ไปใช้ได้เลย — รวม signature verification, event routing, error handling ให้ครบ
      </InfoCard>

      <div className="flex gap-2 mb-6">
        {WEBHOOK_CODEGEN_LANGUAGES.map(item => (
          <button
            key={item.id}
            onClick={() => setCodeLang(item.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
              codeLang === item.id
                ? 'bg-[#0d1117] text-white shadow-lg'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
            }`}
          >
            <FontAwesomeIcon icon={item.icon} />
            {item.label}
          </button>
        ))}
      </div>

      {lang && (
        <CodeBlock id={lang.codeId} label={lang.fileName} code={WEBHOOK_HANDLER_SAMPLES[lang.id]} />
      )}

      <div className="mt-6">
        <InfoCard type="warning" title="สิ่งสำคัญ">
          <ul className="space-y-1 list-disc list-inside">
            <li><strong>ใช้ raw body</strong> ในการ verify — ห้าม parse แล้ว stringify ใหม่ เพราะ key order อาจเปลี่ยน</li>
            <li><strong>ตอบ 200 ทันที</strong> — ถ้า process นานให้ใช้ queue (ระบบจะ retry ถ้าไม่ได้ 2xx ภายใน 10 วินาที)</li>
            <li><strong>Idempotent</strong> — webhook อาจถูกส่งซ้ำ ตรวจสอบ order_number/payout_number ก่อน process</li>
            <li><strong>เก็บ Secret ไว้ใน ENV</strong> — ห้าม hardcode ใน source code</li>
          </ul>
        </InfoCard>
      </div>
    </div>
  );
};

const WebhookToolboxSection: React.FC<{ tools: WebhookToolboxController }> = ({ tools }) => (
  <div>
    <SectionTitle icon={faBell} iconColor="bg-gradient-to-br from-fuchsia-400 to-pink-500">
      Webhook Toolbox
    </SectionTitle>
    <p className="text-gray-500 dark:text-slate-400 mb-6 text-sm">เครื่องมือสำหรับทดสอบและ debug Webhook — ทำงานในเบราว์เซอร์ ปลอดภัย 100%</p>

    {/* Sub-tabs */}
    <div className="flex gap-2 mb-6 border-b border-gray-200 pb-3">
      {TOOL_TABS.map(tab => (
        <button
          key={tab.id}
          onClick={() => tools.setTab(tab.id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            tools.tab === tab.id
              ? 'bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 shadow-sm'
              : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700/50'
          }`}
        >
          <FontAwesomeIcon icon={tab.icon} className="text-xs" />
          {tab.label}
        </button>
      ))}
    </div>

    {tools.tab === 'verifier' && <SignatureVerifier tools={tools} />}
    {tools.tab === 'builder' && <PayloadBuilder tools={tools} />}
    {tools.tab === 'codegen' && <CodeGenerator tools={tools} />}
  </div>
);

export default WebhookToolboxSection;
